/*
 * $Id:: index.js 2021/01/14 03:43 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useRef } from 'react';

import { Button } from '@material-ui/core';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/core/styles';

import { ReactComponent as CalendarIcon } from '../../assets/images/calendar_icon.svg';
import { t } from '../../providers';
import CalendarWrapper from './CalendarWrapper';

const useStyles = makeStyles((theme) => ({
    calendarLabel: {
        paddingRight: '57px',
    },
    calendarIconWithChip: {
        paddingRight: '46px',
        color: '#3F5E20 !important',
    },
    textInput: {
        height: '2rem',
        width: 'auto',
        borderRadius: '4px',
        cursor: 'pointer',
        marginRight: '10px',
        marginLeft: 0,
        backgroundColor: '#ffffff',
        border: '1px solid #9B9B9B',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
        '& input.hasDates': {
            '&::placeholder': {
                color: '#3F5E20 !important',
            },
        },
        '& .MuiSelect-select': {
            paddingRight: '10px',
        },
        '& .MuiButton-label': {
            color: theme.palette.text.chipgrey,
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '0.875rem',
            lineHeight: '142%',
            letterSpacing: '-0.2px',
        },
        '& input': {
            cursor: 'pointer',
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
            '&::placeholder': {
                opacity: '1',
                color: '#5D5D5D',
                fontSize: '0.875rem',
                letterSpacing: '-0.2px',
                fontFamily: theme.typography.button.fontFamily,
            },
        },
        '@media (max-width:600px)': {
            width: '100%',
            marginBottom: '8px',
        },
        '& .MuiSelect-icon': {
            top: 'calc(50% - 6px)',
        },
    },
    doubleCarretIcon: {
        marginRight: '0.7rem',
    },
    calendarPopover: {
        '@media (max-width:600px)': {
            width: '85%',
        },
    },
    applyButton: {
        height: '32px',
        minWidth: '4.5rem',
        color: theme.palette.text.mainGreen,
        border: '1px solid' + theme.palette.text.mainGreen,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderRadius: '2px',
        lineHeight: '12px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    cancelButton: {
        height: '32px',
        minWidth: '4.5rem',
        color: theme.palette.text.chipgrey,
        border: 'none',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderRadius: '2px',
        lineHeight: '12px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
}));

const UARangePicker = (props) => {
    const classes = useStyles();

    const { id, onClose, customInputEndAdornment, cancelRequest = () => {}, applyRequest = () => {}, ...restProps } = props;

    const [range] = useState('');

    const inputEl = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);

    const toggleCalendar = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const closeCalendar = () => {
        setAnchorEl(null);
        cancelRequest();
    };

    const isOpen = Boolean(anchorEl);
    const cancelFilter = () => {
        setAnchorEl(null);
        cancelRequest();
    };
    const applyFilter = (event) => {
        if (restProps && restProps.ranges && restProps.ranges.length > 1) {
            restProps.setDataTypeNotSelected(false);
            setAnchorEl(null);
            applyRequest();
            onClose(event);
        } else {
            restProps.setDataTypeNotSelected(true);
        }
    };

    const toggleVisibilityOnEnter = (event) => {
        if (event.key === 'Enter') {
            toggleCalendar(event);
        }
    };

    return (
        <>
            <Button
                onKeyDown={toggleVisibilityOnEnter}
                aria-haspopup="true"
                aria-controls={id}
                aria-label={t('select.date')}
                aria-expanded={isOpen}
                onClick={toggleCalendar}
                id={`${id}_datepicker`}
                className={classes.textInput}
                disableFocusRipple>
                <>
                    <span className={customInputEndAdornment ? classes.calendarIconWithChip : classes.calendarLabel}>{t('dates')}</span>
                    {customInputEndAdornment ? customInputEndAdornment : <CalendarIcon aria-hidden="true" focusable="false" />}
                </>
            </Button>

            <Popover
                id={id}
                open={isOpen}
                anchorEl={anchorEl}
                onClose={closeCalendar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                classes={{ paper: classes.calendarPopover }}
                style={{ display: isOpen ? 'block' : 'none' }}
                aria-labelledby={`${id}_datepicker`}>
                <CalendarWrapper {...restProps} />
                <div style={{ float: 'right', padding: '0px 17px 17px 0px' }}>
                    <Button id={'cancelButton'} onClick={cancelFilter} className={classes.cancelButton} disableRipple disableFocusRipple>
                        {t('cancel.tab.title')}
                    </Button>
                    <Button id={'applyButton'} onClick={applyFilter} className={classes.applyButton} disableRipple disableFocusRipple>
                        {t('apply.button.text')}
                    </Button>
                </div>
            </Popover>
        </>
    );
};

export default UARangePicker;
